import { calendarDateTimeFromTimestamp, type DateTime, WorkspaceInvitation } from '@rentguru/commons-utils';
import {
  adminClient,
  inferAdditionalFields,
  jwtClient,
  magicLinkClient,
  organizationClient,
  phoneNumberClient,
  twoFactorClient,
} from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import isNil from 'lodash/isNil';

const RESEND_INVITATION_PATH = '/organization/resend-invitation';
const PENDING_REGISTRATIONS_PATH = '/organization/pending-registrations';
const MY_PENDING_INVITATIONS_PATH = '/organization/my-pending-invitations';
const REAUTHENTICATE_PATH = '/reauthenticate';
const REQUEST_ACCOUNT_DELETION_PATH = '/user/request-deletion';
const CANCEL_ACCOUNT_DELETION_PATH = '/user/cancel-deletion';
const TRANSFER_OWNERSHIP_PATH = '/organization/transfer-ownership';
const MEMBERS_PENDING_DELETION_PATH = '/organization/members-pending-deletion';
const SESSION_REFETCH_INTERVAL_SECONDS = 300;

export interface PendingInvitationForCurrentUser {
  id: string;
  organizationId: string;
  organizationName: string;
  role: string;
}

interface OrganizationInvitationRow {
  id: string;
  email: string;
  status: string;
  expiresAt: Date | string | null;
  invitationKind?: string | null;
  roleId?: string | null;
  teamId?: string | null;
  contactId?: string | null;
}

const authClient = createAuthClient({
  plugins: [
    twoFactorClient(),
    adminClient(),
    organizationClient({
      schema: {
        organization: {
          additionalFields: {
            accountType: { type: 'string', required: true, input: true },
            companyName: { type: 'string', required: false, input: true },
            contactId: { type: 'string', required: false },
            generalConditionsAcceptDate: { type: 'date', required: false },
            lastGeneralConditionsAcceptedVersion: { type: 'string', required: false },
            gdprAcceptDate: { type: 'date', required: false },
            lastGdprAcceptedVersion: { type: 'string', required: false },
            pendingDeletion: { type: 'boolean', required: false, input: false },
            pendingDeletionAt: { type: 'date', required: false, input: false },
          },
        },
        member: {
          additionalFields: {
            contactId: { type: 'string', required: false },
            userRoleId: { type: 'string', required: false },
            status: { type: 'string', required: false },
            rootUser: { type: 'boolean', required: false },
            dynamoUserId: { type: 'string', required: false },
            userRightsJson: { type: 'string', required: false, input: false },
          },
        },
        invitation: {
          additionalFields: {
            roleId: { type: 'string', required: false },
            teamId: { type: 'string', required: false },
            contactId: { type: 'string', required: false },
            invitationKind: { type: 'string', required: false },
          },
        },
      },
    }),
    magicLinkClient(),
    jwtClient(),
    phoneNumberClient(),
    inferAdditionalFields({
      user: {
        language: { type: 'string', required: false, input: true },
        firstName: { type: 'string', required: false, input: true },
        lastName: { type: 'string', required: false, input: true },
        pendingDeletion: { type: 'boolean', required: false, input: false },
        pendingDeletionAt: { type: 'date', required: false, input: false },
      },
    }),
  ],
  sessionOptions: {
    refetchInterval: SESSION_REFETCH_INTERVAL_SECONDS,
  },
  fetchOptions: {
    credentials: 'include',
  },
});

export const useSession = authClient.useSession;
export const useActiveOrganization = authClient.useActiveOrganization;
export const useListOrganizations = authClient.useListOrganizations;

export const signInWithEmail = (parameters: Parameters<typeof authClient.signIn.email>[0]) =>
  authClient.signIn.email(parameters);

export const signInWithMagicLink = (parameters: Parameters<typeof authClient.signIn.magicLink>[0]) =>
  authClient.signIn.magicLink(parameters);

export const signInWithSocial = (parameters: Parameters<typeof authClient.signIn.social>[0]) =>
  authClient.signIn.social(parameters);

export const listLinkedAccounts = () => authClient.listAccounts();

export const linkSocialAccount = (parameters: Parameters<typeof authClient.linkSocial>[0]) =>
  authClient.linkSocial(parameters);

export const unlinkSocialAccount = (parameters: Parameters<typeof authClient.unlinkAccount>[0]) =>
  authClient.unlinkAccount(parameters);

export const signUpWithEmail = (parameters: Parameters<typeof authClient.signUp.email>[0]) =>
  authClient.signUp.email(parameters);

export const signOut = () => authClient.signOut();

export const changePassword = (parameters: Parameters<typeof authClient.changePassword>[0]) =>
  authClient.changePassword(parameters);

export const setPassword = (parameters: { newPassword: string }) =>
  authClient.$fetch<{ status: boolean }>('/set-password', { method: 'POST', body: parameters });

export const changeEmail = (parameters: Parameters<typeof authClient.changeEmail>[0]) =>
  authClient.changeEmail(parameters);

export const updateUser = (parameters: Parameters<typeof authClient.updateUser>[0]) =>
  authClient.updateUser(parameters);

export const requestPasswordReset = (parameters: Parameters<typeof authClient.requestPasswordReset>[0]) =>
  authClient.requestPasswordReset(parameters);

export const resetPassword = (parameters: Parameters<typeof authClient.resetPassword>[0]) =>
  authClient.resetPassword(parameters);

export const sendVerificationEmail = (parameters: Parameters<typeof authClient.sendVerificationEmail>[0]) =>
  authClient.sendVerificationEmail(parameters);

export const getAuthClientToken = () => authClient.token();

export const getSession = () => authClient.getSession();

export const stopImpersonating = () => authClient.admin.stopImpersonating();

// Two-factor authentication (TOTP + backup codes)
export const enableTwoFactorRequest = (parameters: Parameters<typeof authClient.twoFactor.enable>[0]) =>
  authClient.twoFactor.enable(parameters);

export const disableTwoFactorRequest = (parameters: Parameters<typeof authClient.twoFactor.disable>[0]) =>
  authClient.twoFactor.disable(parameters);

export const verifyTotp = (parameters: Parameters<typeof authClient.twoFactor.verifyTotp>[0]) =>
  authClient.twoFactor.verifyTotp(parameters);

export const verifyBackupCode = (parameters: Parameters<typeof authClient.twoFactor.verifyBackupCode>[0]) =>
  authClient.twoFactor.verifyBackupCode(parameters);

export const sendPhoneVerificationOtp = (parameters: { phoneNumber: string }) =>
  authClient.phoneNumber.sendOtp(parameters);

export const verifyPhoneNumberOtp = (parameters: { phoneNumber: string; code: string }) =>
  authClient.phoneNumber.verify({ ...parameters, updatePhoneNumber: true, disableSession: true });

export class PhoneVerificationError extends Error {
  code: string | undefined;

  constructor(message: string, code: string | undefined) {
    super(message);
    this.name = 'PhoneVerificationError';
    this.code = code;
  }
}

// Organization wrappers
export const acceptInvitation = (parameters: Parameters<typeof authClient.organization.acceptInvitation>[0]) =>
  authClient.organization.acceptInvitation(parameters);

export const setActiveOrganization = (parameters: Parameters<typeof authClient.organization.setActive>[0]) =>
  authClient.organization.setActive(parameters);

export const listOrganizations = async () => {
  const { data, error } = await authClient.organization.list();
  const organizations = isNil(data)
    ? null
    : data.map((organization) => ({ ...organization, createdAt: toRequiredIsoTimestamp(organization.createdAt) }));
  return { data: organizations, error };
};

export const createOrganization = (parameters: Parameters<typeof authClient.organization.create>[0]) =>
  authClient.organization.create(parameters);

export const updateOrganization = (parameters: Parameters<typeof authClient.organization.update>[0]) =>
  authClient.organization.update(parameters);

// Looking up another space must not flip the active-organization signal: better-auth refetches
// useActiveOrganization after every /organization/* call unless the signal is disabled.
export const getFullOrganization = (organizationId?: string) =>
  isNil(organizationId)
    ? authClient.organization.getFullOrganization()
    : authClient.organization.getFullOrganization({
        query: { organizationId },
        fetchOptions: { disableSignal: true },
      });

export const removeMember = (parameters: Parameters<typeof authClient.organization.removeMember>[0]) =>
  authClient.organization.removeMember(parameters);

type BaseInviteMemberParameters = Parameters<typeof authClient.organization.inviteMember>[0];

type InviteMemberParameters = Omit<BaseInviteMemberParameters, 'role'> & {
  role: string;
  contactId?: string;
  invitationKind?: string;
  roleId?: string;
  teamId?: string;
};

export const inviteMember = (parameters: InviteMemberParameters) =>
  authClient.organization.inviteMember(parameters as BaseInviteMemberParameters);

export const cancelInvitation = (parameters: Parameters<typeof authClient.organization.cancelInvitation>[0]) =>
  authClient.organization.cancelInvitation(parameters);

export const resendMemberInvitation = (parameters: { email: string }) =>
  authClient.$fetch<{ status: boolean }>(RESEND_INVITATION_PATH, { method: 'POST', body: parameters });

export const fetchPendingRegistrations = async (): Promise<Set<string>> => {
  const { data } = await authClient.$fetch<{ emails: string[] }>(PENDING_REGISTRATIONS_PATH, { method: 'GET' });
  return new Set(data?.emails ?? []);
};

export const fetchMyPendingInvitations = async (): Promise<PendingInvitationForCurrentUser[]> => {
  const { data, error } = await authClient.$fetch<{ invitations: PendingInvitationForCurrentUser[] }>(
    MY_PENDING_INVITATIONS_PATH,
    { method: 'GET' }
  );
  if (!isNil(error)) {
    throw error;
  }
  return data?.invitations ?? [];
};

export const fetchPendingInvitations = async (): Promise<Set<string>> => {
  const { data, error } = await authClient.organization.listInvitations();
  if (!isNil(error)) {
    throw error;
  }
  const emails = new Set<string>();
  for (const invitation of data ?? []) {
    if (invitation.status === 'pending' && invitation.email) {
      emails.add(invitation.email.toLowerCase());
    }
  }
  return emails;
};

// Better Auth returns Date instances; GraphQL-facing types carry AWSDateTime strings.
// Kept local on purpose — never lift this into dateutils, JS Date must stay at this SDK boundary.
const toRequiredIsoTimestamp = (value: Date | string): string => (value instanceof Date ? value.toISOString() : value);

export const toIsoTimestamp = (value: Date | string | null | undefined): string | null =>
  isNil(value) ? null : toRequiredIsoTimestamp(value);

export const toDateTime = (value: Date | string | null | undefined): DateTime | null => {
  if (isNil(value)) {
    return null;
  }
  try {
    return calendarDateTimeFromTimestamp(toRequiredIsoTimestamp(value));
  } catch {
    return null;
  }
};

export const fetchOrganizationInvitations = async (): Promise<WorkspaceInvitation[]> => {
  const { data, error } = await authClient.organization.listInvitations();
  if (!isNil(error)) {
    throw error;
  }
  return (data ?? []).map((invitation: OrganizationInvitationRow) => ({
    id: invitation.id,
    email: invitation.email,
    status: invitation.status,
    expiresAt: toIsoTimestamp(invitation.expiresAt),
    invitationKind: invitation.invitationKind ?? null,
    roleId: invitation.roleId ?? null,
    teamId: invitation.teamId ?? null,
    contactId: invitation.contactId ?? null,
  }));
};

// Account & workspace deletion
interface DangerZoneErrorBody {
  code?: string;
  message?: string;
  organizationIds?: string[];
}

export const reauthenticate = (parameters: { password?: string }) =>
  authClient.$fetch<{ proofToken: string }, DangerZoneErrorBody>(REAUTHENTICATE_PATH, {
    method: 'POST',
    body: parameters,
  });

export const requestAccountDeletion = (parameters: { proofToken: string }) =>
  authClient.$fetch<{ purgeAt: string }, DangerZoneErrorBody>(REQUEST_ACCOUNT_DELETION_PATH, {
    method: 'POST',
    body: parameters,
  });

export const cancelAccountDeletion = () =>
  authClient.$fetch<{ status: boolean }, DangerZoneErrorBody>(CANCEL_ACCOUNT_DELETION_PATH, { method: 'POST' });

export const transferOwnership = (parameters: { proofToken: string; targetMemberId: string }) =>
  authClient.$fetch<{ status: boolean }, DangerZoneErrorBody>(TRANSFER_OWNERSHIP_PATH, {
    method: 'POST',
    body: parameters,
  });

export const fetchMembersPendingDeletion = async (): Promise<Set<string>> => {
  const { data } = await authClient.$fetch<{ userIds: string[] }>(MEMBERS_PENDING_DELETION_PATH, { method: 'GET' });
  return new Set(data?.userIds ?? []);
};
